import {
  getPlans,
  getPlansLocalized,
  PLAN_SKU_ANNUAL,
  PLAN_SKU_MONTHLY,
  filterOutGiftSkus,
  GIFT_PLAN_SKUS,
} from 'services/plans'
import { List, Map } from 'immutable'

export const RESET_PLANS_DATA = 'RESET_PLANS_DATA'
export const SET_PLANS_LOCALIZED_PROCESSING = 'SET_PLANS_LOCALIZED_PROCESSING'
export const SET_PLANS_DATA = 'SET_PLANS_DATA'
export const SET_PLANS_SELECTION = 'SET_PLANS_SELECTION'
export const SET_PLANS_PROCESSING = 'SET_PLANS_PROCESSING'
export const SET_PLANS_ERROR = 'SET_PLANS_ERROR'
export const SET_PLAN_CHANGE_SELECTED = 'SET_PLAN_CHANGE_SELECTED'

export function resetPlansData () {
  return {
    type: RESET_PLANS_DATA,
  }
}

/**
 * Get plans data to be used in plan displays.
 * {object} options - The options.
 * {String|String[]} options.language - The language or array of languages.
 * {string} options.currency - A currency
 * {List} options.skus - specific skus to fetch.
 */
export function getPlansData (options) {
  return async function getPlansDataThunk (dispatch, getState) {
    const {
      language,
      currency,
      country,
    } = options

    const { plans: currentPlans, userAccount = Map() } = getState()
    const currentStateProcessing = currentPlans.get('processing')
    const currentStatePlansData = currentPlans.get('data', Map())
    const currentStatePlans = currentStatePlansData.get('plans')

    if (currentStateProcessing
      || ((currentStatePlans && currentStatePlans > 0)
        && currentStatePlansData.get('language') === language)) {
      return currentStatePlansData
    }

    try {
      dispatch(setPlansProccesing(true))
      const data = await getPlans({ language })
      const plans = data.get('plans', List())
      const primarySkus = plans.reduce((acc, plan) => {
        const sku = plan.get('sku')
        if (!GIFT_PLAN_SKUS.includes(sku)) {
          return acc.push(sku)
        }
        return acc
      }, List())
      const giftSkus = plans.reduce((acc, plan) => {
        const sku = plan.get('sku')
        if (GIFT_PLAN_SKUS.includes(sku)) {
          return acc.push(sku)
        }
        return acc
      }, List())
      // We are not done yet, now we need to localize
      // fetch the primary plans for checkout
      const primaryPlansLocalized = await getPlansLocalized({
        language,
        skus: primarySkus,
        defaultPlans: plans,
        countryOverride: country,
        currencyOverride: currency,
        userAccount,
      })
      // all countries can give/redeem a gift and the UI
      // handles unsupported currencies, so we want to fetch them
      // in all situations
      const giftPlansLocalized = await getPlansLocalized({
        language,
        skus: giftSkus,
        defaultPlans: plans,
        countryOverride: 'US',
        currencyOverride: 'USD',
        userAccount,
      })
      const giftPlans = giftPlansLocalized.get('plans', List())
      const primaryPlans = primaryPlansLocalized.get('plans', List())
      const allPlans = primaryPlansLocalized.set('plans', primaryPlans.concat(giftPlans))
      dispatch(setPlansData(allPlans))
      return allPlans
    } catch (e) {
      dispatch(setPlansProccesing(false))
    }
    return Map()
  }
}

/**
 * Get a localized version of the selected plan and set the selection.
 * {string|Map} value - A string SKU will just use fetched data, a Map will cause
 * the fetched data to be merged.
 */
export function setPlansLocalizedSelection (options = {}) {
  return async function setPlansLocalizedSelectionThunk (dispatch, getState) {
    try {
      const {
        sku,
        plan,
        language,
        currency,
      } = options
      const { plans: storePlans } = getState()
      const planSku = sku || (plan && plan.get('sku'))
      const skus = filterOutGiftSkus(List([
        planSku,
        PLAN_SKU_MONTHLY,
        PLAN_SKU_ANNUAL,
      ]))

      let allPlans = storePlans.getIn([
        'data', 'plans',
      ]) || List()

      if (allPlans.size < 1) {
        allPlans = await getPlansData({
          language, currency,
        })(dispatch)
      }
      dispatch(setPlansLocalizedProccesing(true))

      const data = await getPlansLocalized({
        currencyOverride: currency,
        defaultPlans: allPlans,
        language,
        skus,
      })

      const plans = data.get('plans', List()) || List()
      const foundPlan = plans.find((p) => p.get('sku') === planSku)

      if (data.get('_dataError') || plans.size < 1) {
        dispatch(setPlansError(true))
      } else if (planSku && foundPlan && foundPlan.get('checkoutEnabled')) {
        dispatch(setPlansSelection(foundPlan))
      }

      dispatch(setPlansLocalizedProccesing(false))
      return data
    } catch (e) {
      dispatch(setPlansLocalizedProccesing(false))
      dispatch(setPlansError(true))
    }
    return Map()
  }
}

export function setPlansLocalizedProccesing (value) {
  return {
    type: SET_PLANS_LOCALIZED_PROCESSING,
    payload: value,
  }
}

export function setPlansData (data, processing = false) {
  return {
    type: SET_PLANS_DATA,
    payload: { data, processing },
  }
}

export function setPlansSelection (data, processing = false) {
  return {
    type: SET_PLANS_SELECTION,
    payload: { data, processing },
  }
}

export function setPlansProccesing (value) {
  return {
    type: SET_PLANS_PROCESSING,
    payload: value,
  }
}

export function setPlansError (error, processing = false) {
  return {
    type: SET_PLANS_ERROR,
    payload: { error, processing },
  }
}

export function setPlanChangeSelected (data) {
  return {
    type: SET_PLAN_CHANGE_SELECTED,
    payload: data,
  }
}
